/**
 * TimeSpinner — touch-optimised 12-hour time picker with AM/PM toggle
 * and :00/:30 minute toggle. Enforces a minTime floor constraint.
 *
 * Synced mode renders values in blue and disables interaction (used when
 * the CellEditModal is showing the same time applied across multiple days).
 */
import { useEffect, useRef, useState } from 'react';
import { Box, HStack, Pressable, VStack } from 'native-base';

import { Text } from '@pimm/base';
import { hhmm24To12, hhmmToMinutes, to24 } from '../utils';

type Props = {
  label: string;
  value: string;
  onChange: (v: string) => void;
  synced?: boolean;
  syncLabel?: string;
  minTime?: string | null;
};

export const TimeSpinner = ({
  label, value, onChange, synced = false, syncLabel = '', minTime = null,
}: Props) => {
  const init = hhmm24To12(value);
  const [hour,   setHour]   = useState(init.hour);
  const [minute, setMinute] = useState(init.minute);
  const [ampm,   setAmpm]   = useState<'AM' | 'PM'>(init.ampm);
  const prevValue = useRef(value);

  const isBelowMin = (h: number, m: number, ap: 'AM' | 'PM'): boolean => {
    if (!minTime) return false;
    let h24 = h % 12;
    if (ap === 'PM') h24 += 12;
    return h24 * 60 + m < (hhmmToMinutes(minTime) ?? 0);
  };

  const emit = (h: number, m: number, ap: 'AM' | 'PM') => {
    if (!synced) onChange(to24(h, m, ap));
  };

  const spinHour = (delta: number) => {
    setHour((v) => {
      let n = v + delta;
      if (n > 12) n = 1;
      if (n < 1)  n = 12;
      const alreadyBelow = isBelowMin(v, minute, ampm);
      const newBelow     = isBelowMin(n, minute, ampm);
      const blocked      = !alreadyBelow && newBelow;
      if (!blocked) emit(n, minute, ampm);
      return blocked ? v : n;
    });
  };
  const spinMinute = (_delta: number) => {
    setMinute((v) => {
      const n          = v === 0 ? 30 : 0;
      const alreadyBelow = isBelowMin(hour, v, ampm);
      const newBelow     = isBelowMin(hour, n, ampm);
      const blocked      = !alreadyBelow && newBelow;
      if (!blocked) emit(hour, n, ampm);
      return blocked ? v : n;
    });
  };
  const toggleAmpm = (p: 'AM' | 'PM') => {
    if (!isBelowMin(hour, minute, p)) { setAmpm(p); emit(hour, minute, p); }
  };

  useEffect(() => {
    if (value !== prevValue.current && !synced) {
      const p = hhmm24To12(value);
      setHour(p.hour); setMinute(p.minute); setAmpm(p.ampm);
      prevValue.current = value;
    }
  }, [value, synced]);

  const isDisabled = synced;
  const textColor  = synced ? 'blue.700' : 'gray.900';
  const bg         = synced ? 'blue.50'  : 'gray.50';
  const borderClr  = synced ? 'blue.200' : 'gray.200';

  return (
    <VStack space={1} flex={1}>
      <Text size="sm" fontWeight={700} color="gray.600" letterSpacing={1}>
        {label.toUpperCase()}
      </Text>
      <Box bg={bg} borderWidth={1} borderColor={borderClr} rounded="xl" px={3} py={2}>
        <HStack alignItems="center" justifyContent="center" space={2}>
          {/* Hour column */}
          <VStack alignItems="center" space={0}>
            <Pressable onPress={() => !isDisabled && spinHour(1)} opacity={isDisabled ? 0.3 : 1}
              w={9} h={7} alignItems="center" justifyContent="center">
              <Text size="sm" color="gray.400">{'▲'}</Text>
            </Pressable>
            <Text size="xl" fontWeight={700} color={textColor} minW={11} textAlign="center">
              {String(hour).padStart(2, '0')}
            </Text>
            <Pressable onPress={() => !isDisabled && spinHour(-1)} opacity={isDisabled ? 0.3 : 1}
              w={9} h={7} alignItems="center" justifyContent="center">
              <Text size="sm" color="gray.400">{'▼'}</Text>
            </Pressable>
          </VStack>

          <Text size="xl" fontWeight={700} color={textColor} mb={1}>:</Text>

          {/* Minute column */}
          <VStack alignItems="center" space={0}>
            <Pressable onPress={() => !isDisabled && spinMinute(5)} opacity={isDisabled ? 0.3 : 1}
              w={9} h={7} alignItems="center" justifyContent="center">
              <Text size="sm" color="gray.400">{'▲'}</Text>
            </Pressable>
            <Text size="xl" fontWeight={700} color={textColor} minW={11} textAlign="center">
              {String(minute).padStart(2, '0')}
            </Text>
            <Pressable onPress={() => !isDisabled && spinMinute(-5)} opacity={isDisabled ? 0.3 : 1}
              w={9} h={7} alignItems="center" justifyContent="center">
              <Text size="sm" color="gray.400">{'▼'}</Text>
            </Pressable>
          </VStack>

          {/* AM / PM toggle */}
          <VStack space={2} ml={2}>
            {(['AM', 'PM'] as const).map((p) => (
              <Pressable key={p} onPress={() => !isDisabled && toggleAmpm(p)} opacity={isDisabled ? 0.5 : 1}>
                <Box
                  rounded="md" borderWidth={1} alignItems="center"
                  borderColor={ampm === p ? 'primary.500' : 'gray.200'}
                  bg={ampm === p ? 'primary.500' : 'white'}
                  w={12} py={2}
                >
                  <Text size="xs" fontWeight={600} color={ampm === p ? 'black' : 'gray.500'}>
                    {p}
                  </Text>
                </Box>
              </Pressable>
            ))}
          </VStack>
        </HStack>
      </Box>
      {syncLabel ? <Text size="2xs" color="blue.500" mt={0.5}>{syncLabel}</Text> : null}
    </VStack>
  );
};
